import TasksModule from '@modules/TasksModule';
import ServiceModule from '@modules/ServiceModule';
import FrontEndSettingsModule from '@modules/FrontEndSettingsModule';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { InboxContext } from '@modules/InboxModule/types';
import { PcsConversationDetailsResult } from '@api/queries/getPcsConversationDetails';
import { isValidPhoneNumber } from '@pages/settings/phoneNumberUtils';
import Task from '@modules/TasksModule/Task';
import InboxModule from '@modules/InboxModule';
import {
  fetchCampaignForTheCall,
  fetchWorkflowNameForTheCall,
} from '../services';

/**
 * Calculates the agent timeout timestamp based on queue settings
 */
function calculateAgentTimeoutAt(
  queueId: string | undefined,
  sessionOfferedAt: string | null | undefined,
): number | undefined {
  if (!queueId || !sessionOfferedAt) {
    return undefined;
  }

  const queue = ServiceModule.getQueue(queueId);
  if (!queue?.agentTimeout) {
    return undefined;
  }

  const sessionOfferedTime = new Date(sessionOfferedAt).getTime();
  if (Number.isNaN(sessionOfferedTime)) {
    return undefined;
  }

  // Parse "hh:mm:ss" format
  const timeParts = queue.agentTimeout.split(':').map(Number);
  if (timeParts.length !== 3 || timeParts.some((part) => Number.isNaN(part))) {
    return undefined;
  }

  const [hours, minutes, seconds] = timeParts;
  const timeoutSeconds = hours * 3600 + minutes * 60 + seconds;

  if (timeoutSeconds <= 0) {
    return undefined;
  }

  return sessionOfferedTime + timeoutSeconds * 1000;
}

/**
 * Resolves the dialed number from PCS when RTCS delivered no session to carry it, so the
 * Live Services tenant mapping, the workflow name, and the call bubble still work.
 *
 * No-op whenever an RTCS session exists — that path stays authoritative.
 */
function applyDialedNumberFallback(
  task: Task,
  conversationDetails: PcsConversationDetailsResult,
  context: InboxContext,
) {
  const hasRtcsSession = Boolean(task.attributes.sessionId);
  const to = conversationDetails.to;

  if (hasRtcsSession || task.to || !to || !isValidPhoneNumber(to)) {
    return;
  }

  context.logger.info('Resolving dialed number from PCS (no RTCS session)', {
    taskId: task.id,
    to,
  });

  task.to = to;

  if (ServiceModule.isLiveServicesNetwork()) {
    const mappedTenantId = ServiceModule.resolveLiveServicesTenantId(
      Number(task.tenantId),
      to,
    );
    InboxModule.setTenantIdForTask(task, mappedTenantId);
  }

  const isValidTenant = +task.tenantId > 0; // Internal calls carry tenantId "0"

  if (
    FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureDisplayCampaignNameOnTaskAndToast,
    ) &&
    isValidTenant
  ) {
    fetchCampaignForTheCall({
      tenantId: task.tenantId,
      toPhoneNumber: to,
      taskId: task.id,
    });
  }

  if (
    FrontEndSettingsModule.isContactCenterSettingsEnabled(
      'showWorkflowNameOnInboundCalls',
    )
  ) {
    fetchWorkflowNameForTheCall({ toPhoneNumber: to, taskId: task.id });
  }
}

function handleCallDetailsFetched(
  context: InboxContext,
  callId: string,
  conversationDetails: PcsConversationDetailsResult,
) {
  const task = TasksModule.getTaskByCallId(callId);

  if (!task) {
    return;
  }

  applyDialedNumberFallback(task, conversationDetails, context);

  const agentTimeoutAt = calculateAgentTimeoutAt(
    task.queueId,
    conversationDetails.sessionOfferedAt,
  );

  // Always update with conversationDetails
  TasksModule.updateTaskAttributes(task.id, {
    conversationDetails,
    ...(agentTimeoutAt !== undefined && { agentTimeoutAt }),
  });
}

export default handleCallDetailsFetched;
